import React, { useEffect } from 'react';
import { headerHtml, cdnUrls, aemStyles } from './static';
import './static/translate-button.css';
import { getPublicUrl } from '../../utils/publicPath';

const Header = () => {
  useEffect(() => {
    // Add global error handler to catch uncaught errors
    const originalOnError = window.onerror;
    window.onerror = function(message, source, lineno, colno, error) {
      console.log('Global error caught:', {
        message,
        source,
        lineno,
        colno,
        error: error?.stack || error
      });
      // Call original handler if it exists
      if (originalOnError) {
        return originalOnError.apply(this, arguments);
      }
      return false;
    };

    // Add unhandled promise rejection handler
    const originalOnUnhandledRejection = window.onunhandledrejection;
    window.onunhandledrejection = function(event) {
      console.log('Unhandled Promise Rejection:', {
        reason: event.reason,
        stack: event.reason?.stack
      });
      if (originalOnUnhandledRejection) {
        return originalOnUnhandledRejection.apply(this, arguments);
      }
    };

    // Load Bootstrap first (required for dropdown)
    console.log('Loading Bootstrap CSS...');
    const bootstrapCSS = document.createElement('link');
    bootstrapCSS.rel = 'stylesheet';
    bootstrapCSS.href = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css';
    bootstrapCSS.setAttribute('data-aem-header', 'true');
    bootstrapCSS.onload = () => {
      console.log('Bootstrap CSS loaded successfully');
    };
    bootstrapCSS.onerror = (error) => {
      console.error('Failed to load Bootstrap CSS:', error);
    };
    document.head.appendChild(bootstrapCSS);

    // Wait for Bootstrap to load before injecting AEM styles
    bootstrapCSS.onload = () => {
      // Then inject AEM styles
      Object.entries(aemStyles).forEach(([key, css]) => {
        if (css) {
          const style = document.createElement('style');
          style.textContent = css;
          style.setAttribute('data-aem-header', 'true');
          style.setAttribute('data-aem-style', key);
          document.head.appendChild(style);
          console.log(`Injected AEM style: ${key}, Length: ${css.length} characters`);
        }
      });
      console.log(`Total AEM styles injected: ${Object.keys(aemStyles).length}`);
    };

    const loadScript = (url) => {
      return new Promise((resolve, reject) => {
        console.log(`Starting to load script: ${url}`);
        const script = document.createElement('script');
        // Ensure HTTPS for Google Translate script
        script.src = url.startsWith('//') ? `https:${url}` : url;
        script.setAttribute('data-aem-header', 'true');
        
        script.onload = () => {
          console.log(`Successfully loaded script: ${url}`);
          resolve();
        };
        
        script.onerror = (error) => {
          console.error(`Error loading script ${url}:`, error);
          reject(new Error(`Failed to load script ${url}`));
        };
        
        document.body.appendChild(script);
      });
    };

    const initializeTranslate = () => {
      console.log('Initializing translate functionality');
      const translateButton = document.querySelector('.btn-translate');
      if (translateButton) {
        translateButton.disabled = false;

        // Function to inject required styles
        const injectTranslateStyles = () => {
          const styleId = 'dynamic-translate-styles';
          if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
              .custom-translate-dropdown {
                position: absolute;
                background: white;
                border: 1px solid #ccc;
                border-radius: 4px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                z-index: 9999;
                max-height: 300px;
                overflow-y: auto;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              }

              .custom-translate-option {
                padding: 8px 16px;
                cursor: pointer;
                transition: background-color 0.2s;
                font-size: 14px;
                color: #333;
              }

              .custom-translate-option:hover {
                background-color: #f5f5f5;
              }
            `;
            document.head.appendChild(style);
          }
        };

        // Function to load Google Translate script
        const loadGoogleTranslate = () => {
          return new Promise((resolve, reject) => {
            if (window.google?.translate) {
              resolve();
              return;
            }

            // Create a hidden element for Google Translate
            const translateElement = document.createElement('div');
            translateElement.id = 'google_translate_element';
            translateElement.style.display = 'none';
            document.body.appendChild(translateElement);

            // Initialize Google Translate
            window.googleTranslateElementInit = () => {
              new window.google.translate.TranslateElement(
                {
                  pageLanguage: 'en',
                  autoDisplay: false,
                  includedLanguages: 'ar,zh-CN,nl,fr,de,hi,it,ja,ko,pl,pt,ru,es,tr,vi'
                },
                'google_translate_element'
              );
              resolve();
            };

            // Load Google Translate script
            const script = document.createElement('script');
            script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
            script.onerror = reject;
            document.body.appendChild(script);
          });
        };

        // Function to change language
        const doTranslate = (lang) => {
          const translateCombo = document.querySelector('.goog-te-combo');
          if (translateCombo) {
            translateCombo.value = lang;
            translateCombo.dispatchEvent(new Event('change'));
          } else {
            console.error('Translation dropdown not found');
          }
        };

        // Remove any existing click listeners
        const newButton = translateButton.cloneNode(true);
        translateButton.parentNode.replaceChild(newButton, translateButton);

        let isDropdownVisible = false;
        
        newButton.addEventListener('click', async (event) => {
          event.preventDefault();
          event.stopPropagation();
          console.log('Translate button clicked');

          // Toggle dropdown
          if (isDropdownVisible) {
            const existingDropdown = document.querySelector('.custom-translate-dropdown');
            if (existingDropdown) {
              existingDropdown.remove();
            }
            isDropdownVisible = false;
            return;
          }

          // Inject styles
          injectTranslateStyles();

          try {
            // Load Google Translate if not already loaded
            await loadGoogleTranslate();

            // Create language dropdown
            const dropdown = document.createElement('div');
            dropdown.className = 'custom-translate-dropdown';

            const languages = [
              { code: 'ar', name: 'Arabic' },
              { code: 'zh-CN', name: 'Chinese' },
              { code: 'nl', name: 'Dutch' },
              { code: 'fr', name: 'French' },
              { code: 'de', name: 'German' },
              { code: 'hi', name: 'Hindi' },
              { code: 'it', name: 'Italian' },
              { code: 'ja', name: 'Japanese' },
              { code: 'ko', name: 'Korean' },
              { code: 'pl', name: 'Polish' },
              { code: 'pt', name: 'Portuguese' },
              { code: 'ru', name: 'Russian' },
              { code: 'es', name: 'Spanish' },
              { code: 'tr', name: 'Turkish' },
              { code: 'vi', name: 'Vietnamese' }
            ];

            languages.forEach(({ code, name }) => {
              const option = document.createElement('div');
              option.className = 'custom-translate-option';
              option.textContent = name;
              option.addEventListener('click', () => {
                doTranslate(code);
                dropdown.remove();
                isDropdownVisible = false;
              });
              dropdown.appendChild(option);
            });

            // Position dropdown below button
            const buttonRect = newButton.getBoundingClientRect();
            dropdown.style.top = `${buttonRect.bottom}px`;
            dropdown.style.left = `${buttonRect.left}px`;
            document.body.appendChild(dropdown);
            isDropdownVisible = true;

            // Close dropdown when clicking outside
            const handleClickOutside = (e) => {
              if (!dropdown.contains(e.target) && e.target !== newButton) {
                dropdown.remove();
                isDropdownVisible = false;
                document.removeEventListener('click', handleClickOutside);
              }
            };

            // Use setTimeout to avoid immediate trigger
            setTimeout(() => {
              document.addEventListener('click', handleClickOutside);
            }, 0);

          } catch (error) {
            console.error('Failed to initialize translation:', error);
            isDropdownVisible = false;
          }
        });
      }
    };

    const loadScripts = async () => {
      try {
        console.log('Starting to load all required scripts');
        
        // Load jQuery first
        await loadScript('https://code.jquery.com/jquery-3.6.0.min.js');
        
        // Then load Popper.js
        await loadScript('https://cdn.jsdelivr.net/npm/@popperjs/core@2.11.8/dist/umd/popper.min.js');
        
        // Then Bootstrap JS
        await loadScript('https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/js/bootstrap.min.js');

        // Initialize translate button functionality
        initializeTranslate();

        // Initialize Bootstrap dropdowns
        if (window.bootstrap && window.bootstrap.Dropdown) {
          console.log('Initializing Bootstrap dropdowns');
          document.querySelectorAll('[data-bs-toggle="dropdown"]').forEach(dropdownToggle => {
            new window.bootstrap.Dropdown(dropdownToggle);
          });
        }

      } catch (error) {
        console.error('Error in loadScripts:', error);
        // Enable the button anyway so users can try translation
        const translateButton = document.querySelector('.btn-translate');
        if (translateButton) {
          translateButton.disabled = false;
        }
      }
    };

    loadScripts();

    // Cleanup function
    return () => {
      console.log('Cleaning up Header component');
      document.querySelectorAll('[data-aem-header]').forEach(el => {
        el.remove();
      });
      delete window.googleTranslateElementInit;
      const translateElement = document.getElementById('google_translate_element');
      if (translateElement) {
        translateElement.remove();
      }
      document.querySelectorAll('iframe.goog-te-menu-frame').forEach(frame => frame.remove());
      // Restore original error handlers
      window.onerror = originalOnError;
      window.onunhandledrejection = originalOnUnhandledRejection;
      // Remove dynamic styles
      const styleElement = document.getElementById('dynamic-translate-styles');
      if (styleElement) {
        styleElement.remove();
      }
      // Remove any open dropdowns
      const dropdown = document.querySelector('.custom-translate-dropdown');
      if (dropdown) {
        dropdown.remove();
      }
    };
  }, []);

  const updatedHtml = headerHtml
    .replace(/src="\/static\/images\//g, `src="${getPublicUrl('static/images/')}`);

  return (
    <div dangerouslySetInnerHTML={{ __html: updatedHtml }} />
  );
};

export default Header;